import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Briefcase, MapPin, Clock, X } from "lucide-react";
import { toast } from "sonner";

import api from "../../lib/api";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import AdminLayout from "../../components/admin/AdminLayout";
import JobsTable from "../../components/admin/JobsTable";

const isActiveValue = (job) =>
  job.is_active === true ||
  job.is_active === 1 ||
  job.is_active === "1" ||
  job.is_active === "active";

const AdminJob = () => {
  const navigate = useNavigate();

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  // The job pending deletion (drives the confirm dialog).
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // The job currently being viewed in the detail modal.
  const [viewJob, setViewJob] = useState(null);

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const { data } = await api.get("/job-posts");
        // Be tolerant of different response shapes (array, {data}, {jobs}).
        const list = Array.isArray(data)
          ? data
          : data?.data || data?.jobs || [];
        setJobs(list);
      } catch (error) {
        // Table shows its empty state if this fails.
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();
  }, []);

  // Toggle is_active via the status API. Update optimistically, roll back on error.
  const handleToggleStatus = async (job) => {
    const original = job.is_active;
    const newStatus = isActiveValue(job) ? 0 : 1;

    setJobs((prev) =>
      prev.map((j) => (j.id === job.id ? { ...j, is_active: newStatus } : j))
    );

    try {
      await api.post(`/job-posts/${job.id}/status`, { is_active: newStatus });
      toast.success("Status updated");
    } catch (error) {
      // Revert on failure.
      setJobs((prev) =>
        prev.map((j) => (j.id === job.id ? { ...j, is_active: original } : j))
      );
      toast.error(error.response?.data?.message || "Failed to update status");
    }
  };

  const handleEdit = (job) => {
    navigate(`/admin/job-edit/${job.id}`);
  };

  // Confirm + delete via the API.
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.post(`/job-posts/${deleteTarget.id}/delete`);
      // Soft delete — keep the row, just mark it deleted.
      setJobs((prev) =>
        prev.map((j) =>
          j.id === deleteTarget.id
            ? { ...j, deleted_at: j.deleted_at || new Date().toISOString() }
            : j
        )
      );
      toast.success("Job deleted");
      setDeleteTarget(null);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete job");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <AdminLayout
      title="Job Posts"
      actions={
        <Button
          onClick={() => navigate("/admin/job-add")}
          className="gap-2 bg-[#b32126] hover:bg-[#8f1a1f]"
        >
          <Plus className="h-4 w-4" />
          Add Job
        </Button>
      }
    >
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Job Posts</h2>
        <p className="mt-1 text-sm text-gray-600">Manage your job openings.</p>
      </div>

      <JobsTable
        jobs={jobs}
        loading={loading}
        onToggleStatus={handleToggleStatus}
        onView={(job) => setViewJob(job)}
        onEdit={handleEdit}
        onDelete={(job) => setDeleteTarget(job)}
      />

      {/* View job modal */}
      {viewJob && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4"
          onClick={() => setViewJob(null)}
        >
          <div
            className="relative my-10 w-full max-w-2xl rounded-lg bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setViewJob(null)}
              className="absolute right-4 top-4 text-gray-400 hover:text-gray-700"
            >
              <X className="h-5 w-5" />
            </button>

            {/* Header */}
            <div className="border-b px-6 py-5">
              <h3 className="text-xl font-bold text-gray-900">
                {viewJob.job_title || "Untitled"}
              </h3>
              <div className="mt-3 flex flex-wrap gap-3 text-sm text-gray-600">
                {viewJob.role && (
                  <span className="inline-flex items-center gap-1.5">
                    <Briefcase className="h-4 w-4 text-gray-400" />
                    {viewJob.role}
                  </span>
                )}
                {viewJob.location && (
                  <span className="inline-flex items-center gap-1.5">
                    <MapPin className="h-4 w-4 text-gray-400" />
                    {viewJob.location}
                  </span>
                )}
                {viewJob.engagement && (
                  <span className="inline-flex items-center gap-1.5">
                    <Clock className="h-4 w-4 text-gray-400" />
                    {viewJob.engagement}
                  </span>
                )}
                <span
                  className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-medium ${
                    isActiveValue(viewJob)
                      ? "bg-green-50 text-green-700"
                      : "bg-gray-100 text-gray-500"
                  }`}
                >
                  <span
                    className={`h-1.5 w-1.5 rounded-full ${
                      isActiveValue(viewJob) ? "bg-green-500" : "bg-gray-400"
                    }`}
                  />
                  {isActiveValue(viewJob) ? "Active" : "Inactive"}
                </span>
              </div>
            </div>

            {/* Body — description (may be HTML) */}
            <div className="px-6 py-5 max-h-[60vh] overflow-y-auto">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
                Description
              </p>
              {viewJob.job_description ? (
                <div
                  className="prose prose-sm max-w-none text-gray-800"
                  dangerouslySetInnerHTML={{ __html: viewJob.job_description }}
                />
              ) : (
                <p className="text-sm text-gray-400">No description.</p>
              )}
            </div>
          </div>
        </div>
      )}

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && !deleting && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this job?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete{" "}
              <span className="font-medium text-gray-900">
                “{deleteTarget?.job_title || "this job"}”
              </span>
              . This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                confirmDelete();
              }}
              disabled={deleting}
              className="bg-[#b32126] hover:bg-[#8f1a1f]"
            >
              {deleting ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
};

export default AdminJob;
